import { autoinject, observable, bindable, bindingMode } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { getKey, Key } from 'app/shared/lib/keys';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { IHtmlValue, markText } from 'app/shared/lib/formatting/html-utilities';
import { IMenuItem, NavigationService } from 'app/shared/services/navigation-service';
import { searchInOrder, searchAny } from 'app/shared/lib/regexp';

@autoinject
export class EssNavSearch {

    @bindable
    protected menuItems: IMenuItem[] = [];

    @bindable({ defaultBindingMode: bindingMode.fromView })
    protected active: boolean = false;

    @observable
    protected text: string = '';
    protected searchTerms: string[] = [];
    protected results: IMenuItem[] = [];

    protected minSearchLength: number = 2;
    protected maxSearchLength: number = 200;

    constructor(private readonly nav: NavigationService, private readonly env: EnvironmentService, private readonly router: Router) { }

    protected closeSearch(): boolean {
        this.results = [];
        this.text = '';
        return true;
    }

    protected handleSearch(event: Event): void {
        if(this.text && this.results.length > 0) {
            const href = this.searchHref(this.results[0]);
            this.router.navigate(href);
            this.closeSearch();
        }
    }

    protected navKeyDown(event: KeyboardEvent): boolean {
        const key = getKey(event);
        if(key === Key.Esc) {
            this.closeSearch();
            return false;
        }
        return true;
    }

    protected textChanged(): void {
        // trim and truncate to max search length (to prevent DOS)
        const search = this.text.trim().substring(0, this.maxSearchLength);

        // make sure minimum search length criteria is met
        if(search && search.length >= this.minSearchLength) {
            this.searchTerms = search.split(/\s+/);
            // this regexp is not global since we only need to find the first match
            const searchRegExp = searchInOrder(this.searchTerms, 'i');

            this.active = true;
            const results: IMenuItem[] = [];
            this.search(results, this.menuItems, searchRegExp, []);
            this.results = results;
        }
        else {
            this.searchTerms = [];
            this.active = false;
            this.results = [];
        }
    }

    protected searchHref(menuItem: IMenuItem): string {
        return this.nav.resolveUrl(menuItem);
    }

    protected mark(text: string, searchTerms: string[]): IHtmlValue | null {
        // use a new RegExp each time since RegExp objects for global searches have state
        // this regex is global since we want to mark all occurrences
        const searchRegExp = searchAny(searchTerms, 'ig');
        return markText(text, searchRegExp, 'mark', 'ess-search');
    }

    private search(results: IMenuItem[], menuItems: IMenuItem[], searchRegExp: RegExp, groups: string[]): void {
        for(const item of menuItems) {
            if(item.children && item.children.length > 0) {
                let group = item.description || '';
                this.search(results, item.children, searchRegExp, [...groups, group]);
            }
            else {
                if(item.description && item.description.match(searchRegExp)) {
                    (item as any).groups = [...groups];
                    results.push(item);
                }
            }
        }
    }

}
